import type { Dispatch } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { removeAllCalls, removeCall } from '@rsos/reduxDataStore';
import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';
import { DEFAULT_SUPPORTED_LANGUAGES_FALLBACK } from '../TranscriptViewer/ManualLanguageSelector/supportedLanguages';
import {
  addLanguage,
  apiTranscriptionToStateCall,
  findTranslatedText,
  LATEST_SUMMARY_INDEX,
  MAX_CALL_START_TIME_OFFSET,
  TRANSCRIPT_VERSIONS,
  UNKNOWN_SPEAKER,
} from './transcriptionAdapter';
import type { RootState } from './transcriptionRootState';
import type {
  TranscriptionApiGetTranscriptionsResponse,
  TranscriptionApiSpeaker,
  TranscriptionApiToken,
  TranscriptionApiTranscription,
  TranscriptionApiCallInformation,
  TranscriptionRawMessage,
  TranscriptionsState,
  TranscriptionStateSpeaker,
  TranscriptionStateToken,
  TranscriptionSummaryUpdate,
  TranscriptVersion,
  TranscriptionStateCall,
  SetLanguageRequest,
  SetLanguageResponse,
  GetSupportedLanguagesResponse,
} from './transcriptionSlice.types';

export { LATEST_SUMMARY_INDEX, TRANSCRIPT_VERSIONS };

export const transcriptionApi = createApi({
  reducerPath: 'transcriptionApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getAPIHost(),
    prepareHeaders: (headers, { getState }) => {
      const { token } = getTokens() ?? { token: null };
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      const orgName = (getState() as RootState).sinatra?.user?.currentOrg?.name;
      if (orgName) {
        headers.set('X-RapidSOS-Org', orgName);
      }
      return headers;
    },
  }),
  endpoints: builder => ({
    getSupportedLanguages: builder.query<
      GetSupportedLanguagesResponse,
      string | undefined
    >({
      queryFn: async (callerId, _queryApi, _extraOptions, baseQuery) => {
        if (!callerId) {
          return {
            data: {
              languages: DEFAULT_SUPPORTED_LANGUAGES_FALLBACK,
              can_edit: false,
            },
          };
        }

        const result = await baseQuery({
          url: '/v1/rtt_ingress/language',
          params: { caller_id: callerId },
        });

        if (result.error) {
          return {
            data: {
              languages: DEFAULT_SUPPORTED_LANGUAGES_FALLBACK,
              can_edit: false,
            },
          };
        }

        return { data: result.data as GetSupportedLanguagesResponse };
      },
    }),
    setCallerLanguage: builder.mutation<
      SetLanguageResponse,
      SetLanguageRequest
    >({
      query: ({ caller_id, language }) => ({
        url: '/v1/rtt_ingress/language',
        method: 'POST',
        body: { caller_id, language },
      }),
    }),
    fetchTranscriptions: builder.query<
      { transcriptions: TranscriptionApiTranscription[]; totalRecords: number },
      | string
      | {
          callerId: string;
          callStartTime: number | null;
          page?: number;
          limit?: number;
        }
    >({
      keepUnusedDataFor: 0,
      query: arg => {
        const callerId = typeof arg === 'string' ? arg : arg.callerId;
        const limit = typeof arg === 'string' ? 10 : arg.limit || 10;
        const page = typeof arg === 'string' ? 1 : arg.page || 1;
        return `/v1/rtt_egress/calls/transcriptions?caller_id=${callerId}&limit=${limit}&page=${page}`;
      },
      transformResponse: (
        response: TranscriptionApiGetTranscriptionsResponse,
      ) => {
        const raw = response?.transcriptions;
        const transcriptions: TranscriptionApiTranscription[] = Array.isArray(
          raw,
        )
          ? raw
          : raw
            ? [raw]
            : [];

        const totalRecords =
          typeof response?.total_records === 'number'
            ? response.total_records
            : transcriptions.length;

        return { transcriptions, totalRecords };
      },
    }),
  }),
});

const initialState: TranscriptionsState = {
  calls: {},
  transcriptionLists: {},
  transcriptionTotals: {},
  selectedTranscript: TRANSCRIPT_VERSIONS[0],
  autoSelectedTranscript: false,
  maximized: false,
};

const transcriptionsSlice = createSlice({
  name: 'transcriptions',
  initialState,
  reducers: create => ({
    maximize(state) {
      state.maximized = true;
      return state;
    },

    minimize(state) {
      state.maximized = false;
      return state;
    },

    selectTranscript: create.reducer<TranscriptVersion>((state, action) => {
      state.selectedTranscript = action.payload;
      return state;
    }),

    updateTranscriptionToken: create.reducer<{
      callerId: string;
      tokens: TranscriptionApiToken[];
      callInformation: TranscriptionApiCallInformation;
      speakers: TranscriptionApiSpeaker[];
    }>((state, action) => {
      const {
        callerId,
        tokens = [],
        speakers = [],
        callInformation,
      } = action.payload;

      if (!callerId) {
        return;
      }

      if (!Array.isArray(tokens)) {
        return;
      }

      // Ensure the state is initialized for the given callerId
      ensureCallTranscription(state, callerId);

      // If payload is null or undefined, initialize it, and reset its values
      if (!state.calls[callerId].payload) {
        state.calls[callerId].fetching = false;
        state.calls[callerId].error = null;
        state.calls[callerId].payload = {
          tokens: [],
          speakers: [],
          languagesDetected: [],
          call_information: {
            call_start_time: 0,
            call_end_time: 0,
            caller_id: '',
            digi_call_id: '',
          },
          audio_data: { status: 'pending' },
        };
      }

      // Ensure tokens array is always initialized
      if (!Array.isArray(state.calls[callerId].payload.tokens)) {
        state.calls[callerId].payload.tokens = [];
      }

      // If incoming token is from a new call, clear the existing tokens by resetting the payload.
      if (
        callInformation.digi_call_id !==
        state.calls[callerId].payload.call_information.digi_call_id
      ) {
        state.calls[callerId].payload = {
          tokens: [],
          speakers: [],
          languagesDetected: [],
          call_information: callInformation,
          audio_data: { status: 'pending' },
        };
      }

      // Sort incoming tokens by start_time to maintain proper ordering
      const sortedTokens = [...tokens]
        .filter(token => token && typeof token.start_time === 'number')
        .sort((a, b) => a.start_time - b.start_time);

      const existingTokens = state.calls[callerId].payload.tokens;

      // Create a map of existing speakers from the state by id for easy lookup
      const speakersById = (
        state.calls[callerId].payload.speakers || []
      ).reduce<Record<string, TranscriptionStateSpeaker>>((acc, speaker) => {
        acc[speaker.id] = speaker;
        return acc;
      }, {});

      // Update the speakers map with the new speakers
      speakers.forEach(speaker => {
        speakersById[speaker.id] = speaker;
      });

      state.calls[callerId].payload.speakers = Object.values(speakersById);

      // Process each token to include speaker and translations if available
      sortedTokens.forEach(token => {
        const tokenIndex = existingTokens.findIndex(
          t => t.token_id === token.token_id,
        );

        const speaker = speakersById[token.speaker_id] || null;

        const enrichedToken: TranscriptionStateToken = {
          ...token,
          speaker_name: speaker?.name || UNKNOWN_SPEAKER,
          speaker_role: speaker?.role || UNKNOWN_SPEAKER,
          speaker_display_name: speaker?.display_name || UNKNOWN_SPEAKER,
        };

        if (enrichedToken.detected_language !== 'en') {
          state.calls[callerId].payload.languagesDetected = addLanguage(
            state.calls[callerId].payload.languagesDetected,
            enrichedToken.detected_language,
          );

          const translated = `${findTranslatedText(token)}`.trim();
          if (translated) {
            enrichedToken.translated = translated;

            if (!state.autoSelectedTranscript) {
              // only auto-select the translated transcript if it hasn't been
              // manually selected. This is to prevent the transcript from
              // switching back to the original transcript when new tokens
              // are added.
              state.selectedTranscript = TRANSCRIPT_VERSIONS[1];
              state.autoSelectedTranscript = true;
            }
          }
        }

        if (tokenIndex !== -1) {
          // Update the existing token with enriched information
          existingTokens[tokenIndex] = enrichedToken;
        } else {
          // Find the correct position to insert the new token based on start_time
          let insertIndex = existingTokens.findIndex(
            t => t.start_time > token.start_time,
          );
          if (insertIndex === -1) {
            // If no larger start_time is found, append to the end
            insertIndex = existingTokens.length;
          }
          existingTokens.splice(insertIndex, 0, enrichedToken);

          // Detect language changes AFTER inserting the token
          // Now we can compare with the token that's actually before it in the sorted array
          if (token.detected_language) {
            const previousTokenIndex = insertIndex - 1;
            const previousToken =
              previousTokenIndex >= 0
                ? existingTokens[previousTokenIndex]
                : null;

            const previousLanguage = previousToken?.detected_language;
            const currentLanguage = token.detected_language;

            // Detect language changes:
            // 1. First language detection: currentLanguage exists but previousLanguage doesn't
            // 2. Language change: both exist but differ
            const isLanguageChange =
              currentLanguage &&
              (!previousLanguage || previousLanguage !== currentLanguage);

            if (isLanguageChange) {
              if (!state.calls[callerId].languageChangeEvents) {
                state.calls[callerId].languageChangeEvents = [];
              }

              // Check if this matches the manually selected language
              const manualLanguage = state.calls[callerId].manualLanguage;
              const lastShownManualLanguage =
                state.calls[callerId].lastShownManualLanguage;
              const hasShownAutoDetection =
                state.calls[callerId].hasShownAutoDetection ?? false;
              const isManual =
                !!manualLanguage && manualLanguage === currentLanguage;

              // For manual selections: only show divider once per manual language selection
              // For auto-detected: only show divider once ever per call
              // Note: Auto-detection events can be created even after manual selection,
              // but they will be sorted correctly by timestamp (auto-detection will appear before manual)
              const shouldShowDivider = isManual
                ? lastShownManualLanguage !== manualLanguage
                : !hasShownAutoDetection;

              if (shouldShowDivider) {
                // Increment sequence number to maintain insertion order
                const currentSequence =
                  (state.calls[callerId].languageChangeEventsSequenceNumber ??
                    0) + 1;
                state.calls[callerId].languageChangeEventsSequenceNumber =
                  currentSequence;

                // Ensure timestamp is a number (defensive check)
                // Set event timestamp slightly before token start_time so divider appears above the token
                const tokenStartTime =
                  typeof token.start_time === 'number'
                    ? token.start_time
                    : Number(token.start_time);
                // Use tokenStartTime - 1 to ensure divider appears before the token
                const eventTimestamp = tokenStartTime - 1;

                state.calls[callerId].languageChangeEvents.push({
                  timestamp: eventTimestamp,
                  language: currentLanguage,
                  isManual,
                  sequenceNumber: currentSequence,
                });

                // Track that we've shown a divider for this language
                if (isManual) {
                  state.calls[callerId].lastShownManualLanguage =
                    manualLanguage;
                } else {
                  // Mark that we've shown auto-detection for this call
                  state.calls[callerId].hasShownAutoDetection = true;
                }
              }
            }
          }
        }
      });
    }),

    updateTranscriptionSummary: create.reducer<TranscriptionSummaryUpdate>(
      (state, action) => {
        const { callerId, summary = null, keywords = [] } = action.payload;
        const keyword_matches = summary?.keyword_matches || [];

        if (!callerId) {
          return;
        }

        ensureCallTranscription(state, callerId);

        if (summary) {
          const normalizedSummary = {
            ...summary,
            keyword_matches: summary.keyword_matches || [],
          };
          state.calls[callerId].summaries = [
            ...state.calls[callerId].summaries,
            normalizedSummary,
          ];

          // sort summaries so most recent one is always first (other code assumes the latest is first)
          state.calls[callerId].summaries.sort(
            (a, b) => b.generated_time - a.generated_time,
          );

          // remove the oldest summaries if we have too many.
          // Note: I'm not sure we need even >1, but we'll keep several just in case:
          const MAX_SUMMARIES = 10;
          while (state.calls[callerId].summaries.length > MAX_SUMMARIES) {
            state.calls[callerId].summaries.pop();
          }
        }

        if (Array.isArray(keywords)) {
          // replace any existing keywords with new ones
          state.calls[callerId].keywords = keywords;
        }

        if (keyword_matches.length > 0) {
          // replace any existing keyword matches with new ones
          state.calls[callerId].keyword_matches = keyword_matches;
        }

        return state;
      },
    ),

    updateAudioData(state, action) {
      const { callerId, audio_data = {} } = action.payload;

      if (!callerId) {
        return;
      }

      // Ensure the state is initialized for the given callerId
      ensureCallTranscription(state, callerId);
      if (audio_data) {
        state.calls[callerId].payload.audio_data = {
          ...state.calls[callerId].payload.audio_data,
          ...audio_data,
        };
      }
    },

    setSelectedKeywordMatch(
      state,
      action: {
        payload: {
          callerId: string;
          match: {
            keywordSetID: string;
            matchIndex: number;
          } | null;
        };
      },
    ) {
      const { callerId, match } = action.payload;
      ensureCallTranscription(state, callerId);

      state.calls[callerId].selectedKeywordMatch = match;
    },

    setManualLanguage: create.reducer<{
      callerId: string;
      language: string;
    }>((state, action) => {
      const { callerId, language } = action.payload;
      ensureCallTranscription(state, callerId);

      // Only update the UI state for the dropdown
      // Language change events/dividers will be detected from token stream
      const previousManualLanguage = state.calls[callerId].manualLanguage;
      state.calls[callerId].manualLanguage = language;

      // Reset lastShownManualLanguage if the user changed to a different language
      // This ensures we show a divider when tokens start coming in with the new language
      // Note: We don't disable auto-detection here - it will still show for tokens with earlier timestamps
      // Events are sorted by timestamp, so auto-detection will always appear before manual selection
      if (previousManualLanguage !== language) {
        state.calls[callerId].lastShownManualLanguage = undefined;
      }
    }),
  }),

  extraReducers: builder => {
    builder
      .addCase(
        removeCall,
        (state: TranscriptionsState, action: { payload: string }) => {
          delete state.calls[action.payload];
          delete state.transcriptionLists[action.payload];
          delete state.transcriptionTotals[action.payload];
        },
      )
      .addCase(removeAllCalls, () => {
        return initialState;
      })
      .addMatcher(
        transcriptionApi.endpoints.fetchTranscriptions.matchPending,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerId =
            typeof originalArgs === 'string'
              ? originalArgs
              : originalArgs.callerId;
          const page =
            typeof originalArgs === 'string' ? 1 : originalArgs.page || 1;

          // Don't flip the main transcript viewer into "fetching" when we're just loading more history.
          if (page > 1) return;
          state.calls[callerId] = {
            ...state.calls[callerId],
            fetching: true,
            error: null,
            payload: {
              ...(state.calls[callerId]?.payload || {
                audio_data: { status: 'pending' },
                languagesDetected: [],
                call_information: {
                  call_start_time: 0,
                  call_end_time: 0,
                  caller_id: '',
                  digi_call_id: '',
                },
              }),
            },
            summaries: state.calls[callerId]?.summaries || [],
            keywords: state.calls[callerId]?.keywords || [],
            keyword_matches: state.calls[callerId]?.keyword_matches || [],
          };
        },
      )
      .addMatcher(
        transcriptionApi.endpoints.fetchTranscriptions.matchFulfilled,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerId =
            typeof originalArgs === 'string'
              ? originalArgs
              : originalArgs.callerId;
          const callStartTime =
            typeof originalArgs === 'string'
              ? null
              : originalArgs.callStartTime;
          const page =
            typeof originalArgs === 'string' ? 1 : originalArgs.page || 1;
          const transcriptions: TranscriptionApiTranscription[] =
            action.payload?.transcriptions || [];
          const totalRecords =
            typeof action.payload?.totalRecords === 'number'
              ? action.payload.totalRecords
              : null;

          ensureCallTranscription(state, callerId);

          const sortedByStartDesc = [...transcriptions].sort((a, b) => {
            const bt = Number(b?.call_information?.call_start_time || 0);
            const at = Number(a?.call_information?.call_start_time || 0);
            return bt - at;
          });

          // Persist the full list so selectors/UI can show older transcripts.
          if (page <= 1) {
            state.transcriptionLists[callerId] = sortedByStartDesc;
          } else {
            const existing = state.transcriptionLists[callerId] || [];
            const merged = [...existing, ...sortedByStartDesc];
            const byKey = new Map<string, TranscriptionApiTranscription>();
            merged.forEach(t => {
              const key =
                String(t?.call_information?.digi_call_id || '') ||
                String(t?.call_information?.call_start_time || '');
              if (key) byKey.set(key, t);
            });
            const uniqueList = Array.from(byKey.values());
            state.transcriptionLists[callerId] = uniqueList.sort(
              (a, b) =>
                Number(b?.call_information?.call_start_time || 0) -
                Number(a?.call_information?.call_start_time || 0),
            );
          }
          if (typeof totalRecords === 'number') {
            state.transcriptionTotals[callerId] = totalRecords;
          }

          // Only update `calls[callerId]` from egress on the first page fetch.
          if (page <= 1) {
            // For the "current transcript", only seed from a transcription that matches the current call.
            // If the most recent transcription is from a previous call (> 10 min offset), do NOT save it into `calls`.
            const isKnownCallStartTime =
              typeof callStartTime === 'number' && callStartTime > 0;

            const latestForCurrentCall = isKnownCallStartTime
              ? sortedByStartDesc.find(t => {
                  const tStart = Number(
                    t?.call_information?.call_start_time || 0,
                  );
                  return callStartTime - tStart < MAX_CALL_START_TIME_OFFSET;
                }) || null
              : sortedByStartDesc[0] || null;

            const currentState = latestForCurrentCall
              ? apiTranscriptionToStateCall(latestForCurrentCall)
              : nullTranscript();

            // Preserve UI-only fields for the currently selected call (manual language, dividers, etc.)
            const prevUi = state.calls[callerId] || nullTranscript();
            currentState.manualLanguage = prevUi.manualLanguage;
            currentState.languageChangeEvents = prevUi.languageChangeEvents;
            currentState.lastShownManualLanguage =
              prevUi.lastShownManualLanguage;
            currentState.hasShownAutoDetection = prevUi.hasShownAutoDetection;
            currentState.languageChangeEventsSequenceNumber =
              prevUi.languageChangeEventsSequenceNumber;

            currentState.fetching = false;
            currentState.error = null;
            state.calls[callerId] = currentState;
          }
        },
      )
      .addMatcher(
        transcriptionApi.endpoints.fetchTranscriptions.matchRejected,
        (state, action) => {
          const originalArgs = action.meta.arg.originalArgs;
          const callerId =
            typeof originalArgs === 'string'
              ? originalArgs
              : originalArgs.callerId;
          const page =
            typeof originalArgs === 'string' ? 1 : originalArgs.page || 1;

          // Don't surface "unavailable" errors in the main transcript viewer when loading more history.
          if (page > 1) return;
          state.calls[callerId] = {
            ...state.calls[callerId],
            fetching: false,
            error: action.error.message,
            payload: {
              ...state.calls[callerId]?.payload,
              audio_data: { status: 'unavailable' },
            },
          };
        },
      );
  },
});

export const {
  selectTranscript,
  updateTranscriptionToken,
  updateTranscriptionSummary,
  updateAudioData,
  setSelectedKeywordMatch,
  setManualLanguage,
  maximize,
  minimize,
} = transcriptionsSlice.actions;

export const handleTranscriptionRawMessage =
  (message: TranscriptionRawMessage | null = {}) =>
  (dispatch: Dispatch, getState: () => Omit<RootState, 'emergencyCalls'>) => {
    // Extract the callerID from the topic
    const parts = `${message?.topic}`.split('tel://');
    let callerId = '';
    if (parts.length > 1) {
      callerId = parts[1];
    }

    // Try to find any tokens in the message body
    let tokens: TranscriptionApiToken[] = [];
    if (message?.body?.tokens) {
      tokens = message.body.tokens;
    } else if (message?.body?.token) {
      tokens = [message.body.token];
    } else if (message?.body?.token_id || message?.body?.transcript) {
      tokens = [message.body];
    }

    const speakers = message?.speakers || message?.body?.speakers || [];

    const callInformation = message?.call_data || {
      call_start_time: 0,
      call_end_time: 0,
      caller_id: '',
      digi_call_id: '',
    };

    // If we receive tokens and the status is still pending, set to in_progress
    if (tokens.length > 0) {
      const state = getState();
      const currentStatus =
        state.transcriptions?.calls?.[callerId]?.payload?.audio_data?.status;
      if (currentStatus === 'pending') {
        dispatch(
          updateAudioData({
            callerId,
            audio_data: { status: 'in_progress' },
          }),
        );
      }
    }

    dispatch(
      transcriptionsSlice.actions.updateTranscriptionToken({
        callerId,
        callInformation,
        tokens,
        speakers,
      }),
    );
  };

function nullTranscript(): TranscriptionStateCall {
  return {
    fetching: false,
    error: null,
    payload: {
      tokens: [],
      speakers: [],
      audio_data: { status: 'pending' },
      languagesDetected: [],
      call_information: {
        call_start_time: 0,
        call_end_time: 0,
        caller_id: '',
        digi_call_id: '',
      },
    },
    summaries: [],
    keywords: [],
    keyword_matches: [],
    selectedKeywordMatch: null,
    manualLanguage: null,
    languageChangeEvents: [],
    lastShownManualLanguage: undefined,
    hasShownAutoDetection: false,
  };
}

function ensureCallTranscription(state: TranscriptionsState, callerId: string) {
  if (!state.calls[callerId]) {
    state.calls[callerId] = nullTranscript();
  }
  if (!state.transcriptionLists[callerId]) {
    state.transcriptionLists[callerId] = [];
  }
}

export * from './transcriptionSelectors';

export const {
  useFetchTranscriptionsQuery,
  useSetCallerLanguageMutation,
  useGetSupportedLanguagesQuery,
} = transcriptionApi;
export const transcriptionsReducer = transcriptionsSlice.reducer;

export const transcriptionApiReducer = transcriptionApi.reducer;

export default transcriptionsReducer;
